import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useSearchState
 * Encapsulates debounced search logic.
 * Components receive results and query — no axios code needed in components.
 */
export function useSearchState(open) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Cookies sent automatically
      const res = await axios.get(`${API_BASE}/search`, { params: { q } });
      setResults(res.data);
    } catch (err) {
      console.error('Search failed:', err);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = useCallback((val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 280);
  }, [doSearch]);

  const reset = useCallback(() => {
    setQuery('');
    setResults(null);
    setLoading(false);
    clearTimeout(debounceRef.current);
  }, []);

  const totalResults = results
    ? (results.action_cards?.length || 0)
      + (results.tasks?.length || 0)
      + (results.contacts?.length || 0)
    : 0;

  return { query, results, loading, handleChange, reset, totalResults };
}
